import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0F0E] flex items-center justify-center p-4">
        <div className="bg-[#111413] border border-white/10 rounded-2xl p-8 max-w-md text-center">
          <h2 className="text-white text-xl font-bold mb-3">Invalid Reset Link</h2>
          <p className="text-white/50 text-sm mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password">
            <Button className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white">Request New Link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F0E] flex items-center justify-center p-4">
      <SEO title="Reset Password" description="Set a new password for your BCH account" path="/reset-password" />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/"><img src="/bch-logo.png" alt="BCH" className="h-20 mx-auto mb-4" /></Link>
        </div>
        <div className="bg-[#111413] border border-white/10 rounded-2xl p-8">
          {!done ? (
            <>
              <h1 className="text-white text-xl font-bold mb-2">Set New Password</h1>
              <p className="text-white/50 text-sm mb-6">Enter your new password below.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input type={showPw ? "text" : "password"} placeholder="New password (min 6 chars)"
                    value={password} onChange={e => setPassword(e.target.value)}
                    className="bg-white/5 border-white/10 text-white pl-10 pr-10 h-12"
                    data-testid="new-password-input" required minLength={6} />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                    {showPw ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input type={showPw ? "text" : "password"} placeholder="Confirm new password"
                    value={confirm} onChange={e => setConfirm(e.target.value)}
                    className="bg-white/5 border-white/10 text-white pl-10 h-12"
                    data-testid="confirm-password-input" required minLength={6} />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white font-semibold" data-testid="reset-submit-btn">
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-[#2A9D8F] mx-auto mb-4" weight="fill" />
              <h2 className="text-white text-xl font-bold mb-2">Password Reset!</h2>
              <p className="text-white/50 text-sm mb-6">Your password has been updated. You can now sign in.</p>
              <Button onClick={() => navigate("/login")} className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white w-full h-12 font-semibold" data-testid="go-to-login-btn">
                Go to Sign In
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
